import cookie from 'js-cookie'
import { InfaceAuthError } from '@inface-sdk/web-auth'
import { GW_PATH, NX_LOCAL_PLATFORM } from '../../constants'
import { getParameterByName } from '../../utils/variables'

/** @typedef JP_TPA_SERVICES */
const JP_TPA_SERVICES = ['tpa.google', 'tpa.apple', 'tpa.line', 'tpa.facebook', 'tpa.twitter']
const NEXON_SERVICE = 'nexon'
const ALLOW_SERVICES = [NEXON_SERVICE, ...JP_TPA_SERVICES]

export default class NxPlug {
  /** @type {'signin'|'signout'} signin, signout 등 NXPLUG 의 진행중인 상황 */
  #phase
  /** @type {string} 환경별 INSIGN domain, ex: https://signin.nexon.com */
  #insignOrigin
  #platform
  get platform() { return this.#platform }
  #locale
  get locale() { return this.#locale }
  /** @type {'nxplug'} */
  #type
  /** @type {'nexon'|'tpa.google'|'tpa.apple'|'tpa.line'|'tpa.facebook'|'tpa.twitter'}  */
  #authService
  /** @type {string} - valid URL format */
  #authUrl
  /** @type {'local'|'qa'|'pre'|'live'} */
  #plugWebEnv

  constructor({
    phase = '',
    insignOrigin = 'https://signin.nexon.com',
    platform, locale, type,
    auth_service: authService = getParameterByName('auth_service'),
    auth_url: authUrl = getParameterByName('auth_url'),
    plug_web_env: plugWebEnv = getParameterByName('plug_web_env'),
  } = {}) {
    this.#phase = phase
    this.#insignOrigin = insignOrigin
    this.#platform = platform
    this.#locale = locale
    this.#type = type
    this.#authService = authService
    this.#plugWebEnv = plugWebEnv || 'live'

    if (authService && authUrl) {
      this.#authUrl = decodeURIComponent(authUrl)
      if (!this.#validateAuthInfo()) {
        throw new InfaceAuthError({ code: 113513, name: 'INVALID PARAMETERS' })
      }
    }
  }

  gotoSignIn() {
    if (this.#isJppc()) {
      window.location.href = this.#getJPPCLoginUrl()
    } else {
      // TODO 파라미터가 잘못 된 올바르지 않은 접근에 대한 코드 정의 필요
      throw new InfaceAuthError({ code: 113514, name: 'INVALID PARAMETERS' })
    }
  }

  processSignIn() {
    const npp = cookie && cookie.get('NPP')
    const isSignedIn = !!npp
    if (this.#authService === NEXON_SERVICE) {
      this.#sendPostMessage(npp)
      return // NJ ID/PW 로그인 후 postMessage 수신 후 plug-web 에서 컨트롤
    }
    if (JP_TPA_SERVICES.includes(this.#authService)) {
      this.#sendNxPlugScheme(npp)
    }
    this.#locateClosePageBySigninResult(isSignedIn)
  }

  #sendPostMessage(npp) {
    if (this.#isJppc()) {
      if (window !== window.parent) {
        const plugOrigin = this.#getPlugOrigin()
        if (plugOrigin) {
          window.parent.postMessage(JSON.stringify({
            success: !!npp,
            data: { npp },
          }), plugOrigin)
        }
      }
    }
  }

  #sendNxPlugScheme(npp) {
    if (this.#isJppc()) {
      console.debug(`스킴 호출 w/NPP[${npp}]`)
      window.location.href = `nexonplug://NexonAuthToken/?token=${npp}`
    }
  }

  #locateClosePageBySigninResult(isSignedIn) {
    const login = isSignedIn ? 'success' : 'fail'
    const params = new URLSearchParams({ login })
    this.#locale && params.append('locale', this.#locale)

    window.location.href = `${this.#insignOrigin}/close?${params.toString()}` // alert 띄운 후 에러 페이지로 이동
  }

  #getJPPCLoginUrl() {
    const redirectUrl = this.#getJPPCSsoUrl()
    const url = new URL(this.#authUrl)
    const redirectQueryName = this.#authService === NEXON_SERVICE ? 'url' : 'redirect'
    url.searchParams.append(redirectQueryName, redirectUrl) // URLSearchParam이 encoding 함
    return url.toString()
  }

  #getJPPCSsoUrl() {
    const insignUrl = new URL(this.#insignOrigin)
    insignUrl.pathname = GW_PATH.SSO_JPPC
    insignUrl.searchParams.append('type', 'nxplug')
    insignUrl.searchParams.append('platform', NX_LOCAL_PLATFORM.JPPC)
    insignUrl.searchParams.append('auth_service', this.#authService)
    if (this.#authService === NEXON_SERVICE) { // NX일 경우만 postMessage()
      insignUrl.searchParams.append('plug_web_env', this.#plugWebEnv)
    }
    this.#locale && insignUrl.searchParams.append('locale', this.#locale)
    return insignUrl.toString()
  }

  #getPlugOrigin() {
    const PLUG_ORIGIN = {
      live: 'https://assets-nxplug.nexon.com',
      pre: 'https://pre-assets-nxplug.nexon.com',
      qa: 'https://test-assets-nxplug.nexon.com',
      local: 'https://assets-local-nxplug.nexon.com',
    }

    return PLUG_ORIGIN[this.#plugWebEnv]
  }

  #isJppc() { return this.#platform === NX_LOCAL_PLATFORM.JPPC }

  #validateAuthInfo() {
    if (this.#isJppc()) {
      return this.#validateJPPCAuthSignin()
    }
    return false
  }

  #validateJPPCAuthSignin() {
    if (!ALLOW_SERVICES.includes(this.#authService)) return false

    try {
      const url = new URL(this.#authUrl)
      return url.hostname.includes('jp.nexon.com')
    } catch (error) {
      return false
    }
  }
}
